import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ServiceStep from './steps/ServiceStep';
import PropertyStep from './steps/PropertyStep';
import ExtrasStep from './steps/ExtrasStep';
import ScheduleStep from './steps/ScheduleStep';
import ContactStep from './steps/ContactStep';
import ReviewPayStep from './steps/ReviewPayStep';
import ConfirmationScreen from './steps/ConfirmationScreen';
import CartSummary from './steps/CartSummary';
import './BookingWizard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = ['Service', 'Property', 'Extras', 'Schedule', 'Contact', 'Review & Pay'];
const REVIEW_STEP = 5;
const CONTACT_STEP = 4;
const SCHEDULE_STEP = 3;

const emptyDraft = {
  service_type: '',
  address: '',
  zip_code: '',
  square_feet: '',
  bedrooms: 2,
  bathrooms: 2,
  add_ons: {},
  frequency: 'one_time',
  date: '',
  time_window: '',
  notes: '',
  property_verified: false,
  property_details_note: '',
};

const BookingWizard = () => {
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState({ ...emptyDraft });
  const [cart, setCart] = useState([]);
  const [contact, setContact] = useState({
    name: '', email: '', phone: '', company: '',
    is_property_manager: false, access_notes: '',
  });
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/config`)
      .then((r) => r.json())
      .then((cfg) => {
        setConfig(cfg);
        const preSvc = searchParams.get('service');
        if (preSvc && cfg?.services?.some((s) => s.key === preSvc)) {
          setDraft((p) => ({ ...p, service_type: preSvc }));
        }
      })
      .catch(() => {});
  }, []);

  const updateDraft = (updates) => setDraft((p) => ({ ...p, ...updates }));
  const updateContact = (updates) => setContact((p) => ({ ...p, ...updates }));

  const contactComplete = contact.name && contact.email && contact.phone;

  const nextStep = () => {
    let next = step + 1;
    if (step === SCHEDULE_STEP && contactComplete && cart.length > 0) next = REVIEW_STEP;
    setStep(Math.min(next, REVIEW_STEP));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    let prev = step - 1;
    if (step === REVIEW_STEP && contactComplete && cart.length > 0) prev = SCHEDULE_STEP;
    setStep(Math.max(prev, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addAnotherProperty = () => {
    setCart([...cart, draft]);
    setDraft({ ...emptyDraft });
    setStep(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const removeCartItem = (idx) => {
    setCart(cart.filter((_, i) => i !== idx));
  };

  if (!config) {
    return (
      <div className="booking-wizard">
        <div className="wizard-loading" data-testid="wizard-loading">
          <div className="spinner" />
          <p>Loading booking options...</p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="booking-wizard">
        <ConfirmationScreen bookingId={confirmed.bookingId} />
      </div>
    );
  }

  const stepProps = { config, draft, updateDraft, nextStep, prevStep };

  return (
    <div className="booking-wizard">
      <div className="wizard-header">
        <h1 className="wizard-title">Book Your Cleaning</h1>
        <p className="wizard-subtitle">Premium service, transparent pricing — done in minutes</p>
      </div>

      <div className="step-indicator" data-testid="wizard-step-indicator">
        {STEPS.map((name, i) => (
          <div
            key={name}
            className={`step-item ${i === step ? 'active' : i < step ? 'complete' : ''}`}
          >
            <div className="step-circle">
              {i < step ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                i + 1
              )}
            </div>
            <span className="step-name">{name}</span>
          </div>
        ))}
      </div>

      <div className="wizard-content">
        <div className="wizard-form-col">
          <div className="form-card">
            {step === 0 && <ServiceStep {...stepProps} />}
            {step === 1 && <PropertyStep {...stepProps} />}
            {step === 2 && <ExtrasStep {...stepProps} />}
            {step === 3 && <ScheduleStep {...stepProps} />}
            {step === 4 && (
              <ContactStep
                contact={contact}
                updateContact={updateContact}
                nextStep={nextStep}
                prevStep={prevStep}
              />
            )}
            {step === REVIEW_STEP && (
              <ReviewPayStep
                config={config}
                cart={cart}
                draft={draft}
                contact={contact}
                removeCartItem={removeCartItem}
                addAnotherProperty={addAnotherProperty}
                prevStep={prevStep}
                onSuccess={(payment) => setConfirmed({ bookingId: payment.bookingId })}
              />
            )}
          </div>
        </div>

        {step < REVIEW_STEP && (
          <div className="wizard-summary-col">
            <CartSummary config={config} cart={cart} draft={draft} />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingWizard;
